import { StandardEvents } from '@shopify/events';

// Only collection HTML is fetched here. Native Shopify ProductFormComponent owns cart requests.
class OdCatalogBrowser extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.categoryFrame);
    this.categoryFrame = 0;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.results = this.querySelector('[data-od-results]');
    this.status = this.querySelector('[data-od-catalog-status]');
    this.errorBox = this.querySelector('[data-od-load-error]');
    this.categories = this.querySelector('[data-od-categories]');
    this.categoryPrevious = this.querySelector('[data-od-category-prev]');
    this.categoryNext = this.querySelector('[data-od-category-next]');
    this.categoryControls = this.querySelector('[data-od-category-controls]');
    this.pending ??= new Map();
    for (const [form, record] of this.pending) {
      if (!record.timer) record.timer = setTimeout(() => this.settle(form, false, this.dataset.odError), Math.max(0, 30000 - (Date.now() - record.startedAt)));
    }
    const parameter = new URLSearchParams(location.search).get('c');
    const chosen = [...this.querySelectorAll('[data-od-category]')].find(link => link.dataset.handle === parameter);
    this.nativeCollectionURL = this.resource(this.dataset.nativeCollectionUrl);
    this.resourceURL = this.resource(chosen?.href || this.dataset.initialUrl);
    // Direct collection links carry native sort/filter/page parameters without a catalogue c key.
    if (this.resourceURL && (chosen || this.nativeCollectionURL?.pathname === this.resourceURL.pathname)) {
      for (const [key, value] of new URLSearchParams(location.search)) {
        if (this.resultParameter(key)) this.resourceURL.searchParams.append(key, value);
      }
    }
    this.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[data-od-category],a[data-od-filter-link],a[data-od-page]');
      if (link && this.contains(link) && !link.hasAttribute('download') && (!link.target || link.target === '_self')) {
        const url = this.resource(link.href);
        if (!url) return;
        event.preventDefault();
        this.load(url, link.hasAttribute('data-od-category') ? 'category' : 'results');
      }
      if (event.target.closest('[data-od-retry]') && this.lastAttempt) this.load(this.lastAttempt, 'results');
      if (event.target.closest('[data-od-category-prev]')) this.scrollCategories(-1);
      if (event.target.closest('[data-od-category-next]')) this.scrollCategories(1);
    }, options);
    this.addEventListener('submit', event => {
      if (!event.target.matches('[data-od-filter-form]')) return;
      event.preventDefault();
      this.loadForm(event.target);
    }, options);
    this.addEventListener('change', event => {
      const form = event.target.closest('[data-od-filter-form]');
      if (form) this.loadForm(form);
    }, options);
    // Window capture runs before the native document-capture delegated submit handler.
    window.addEventListener('submit', event => this.guardSubmit(event), { ...options, capture: true });
    this.addEventListener(StandardEvents.cartLinesUpdate, event => this.observeCart(event), options);
    this.addEventListener(StandardEvents.cartError, event => this.observeCartError(event), options);
    this.categories?.addEventListener('keydown', event => {
      if (event.target !== this.categories) return;
      const horizontal = this.categoryHorizontal();
      const previous = horizontal ? 'ArrowLeft' : 'ArrowUp';
      const next = horizontal ? 'ArrowRight' : 'ArrowDown';
      if ([previous, next].includes(event.key)) { event.preventDefault(); this.scrollCategories(event.key === next ? 1 : -1); }
      if (['Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const end = horizontal ? this.categories.scrollWidth : this.categories.scrollHeight;
        this.categories.scrollTo({ [horizontal ? 'left' : 'top']: event.key === 'Home' ? 0 : end, behavior: 'auto' });
      }
    }, options);
    this.categories?.addEventListener('scroll', () => this.scheduleCategoryUpdate(), { ...options, passive: true });
    if ('ResizeObserver' in window && this.categories) {
      this.resize = new ResizeObserver(() => this.scheduleCategoryUpdate());
      this.resize.observe(this.categories);
    }
    this.updateCategoryControls();
    const initialResult = this.initialServerResult();
    this.connectedOnce = true;
    if (initialResult) {
      this.lastAttempt = new URL(this.resourceURL);
      this.errorBox.hidden = true;
      this.completeSelection(this.resourceURL, initialResult);
    } else if (this.resourceURL) this.load(this.resourceURL, 'initial');
  }

  initialServerResult() {
    // Only an untouched, first native-collection render with no result query is
    // provably the same response. Deep links and editor reconnects still fetch.
    if (this.connectedOnce || !this.nativeCollectionURL || !this.resourceURL || !this.results || this.results.hasAttribute('aria-busy')) return null;
    const page = new URL(location.href);
    if (page.pathname !== this.nativeCollectionURL.pathname || this.resourceURL.pathname !== page.pathname) return null;
    if ([...page.searchParams.keys(), ...this.resourceURL.searchParams.keys()].some(key => key === 'c' || this.resultParameter(key))) return null;
    const result = this.results.querySelector('[data-od-catalog-result]');
    if (!result || this.resource(result.dataset.collectionUrl)?.pathname !== page.pathname || !result.dataset.collectionHandle || !/^\d+$/.test(result.dataset.productsCount || '')) return null;
    const form = result.querySelector('[data-od-filter-form]');
    const sort = form?.querySelector('[data-od-sort]');
    const serverSort = sort?.querySelector('option[selected]');
    if (!sort || !serverSort || sort.value !== serverSort.value) return null;
    for (const input of form.querySelectorAll('[name^="filter."]')) {
      if (['checkbox', 'radio'].includes(input.type) ? input.checked : String(input.value).trim() !== '') return null;
    }
    const currentPage = result.querySelector('[aria-current="page"]');
    if (currentPage && currentPage.textContent.trim() !== '1') return null;
    return result;
  }

  completeSelection(url, result) {
    this.resourceURL = new URL(url);
    this.rememberSelection(url, result.dataset.collectionHandle);
    this.querySelectorAll('[data-od-category]').forEach(link => {
      if (link.dataset.handle === result.dataset.collectionHandle) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    this.status.textContent = `${this.dataset.loadedLabel}: ${result.dataset.productsCount} ${this.dataset.productsLabel}.`;
  }

  resource(href) {
    if (!href) return null;
    const url = new URL(href, location.href);
    if (url.origin !== location.origin || !/\/collections\/[^/]+\/?$/.test(url.pathname)) return null;
    url.hash = '';
    url.searchParams.delete('section_id');
    return url;
  }

  resultParameter(key) {
    return key === 'sort_by' || key === 'page' || key.startsWith('filter.');
  }

  rememberSelection(url, handle) {
    // Keep the catalogue route so reload and returning from a product restore this view.
    const page = new URL(location.href);
    for (const key of [...page.searchParams.keys()]) {
      if (key === 'c' || this.resultParameter(key)) page.searchParams.delete(key);
    }
    if (this.nativeCollectionURL?.pathname !== url.pathname) page.searchParams.set('c', handle);
    for (const [key, value] of url.searchParams) {
      if (this.resultParameter(key)) page.searchParams.append(key, value);
    }
    history.replaceState(history.state, '', page);
  }

  loadForm(form) {
    const url = this.resource(form.action);
    if (!url || !form.reportValidity()) return;
    url.search = '';
    for (const [key, value] of new FormData(form)) if (String(value).trim() !== '') url.searchParams.append(key, String(value));
    url.searchParams.delete('page');
    this.load(url, 'filter');
  }

  cartBusy() {
    if (!this.pending.size) return false;
    this.status.textContent = 'Espera a que termine la operación del carrito y vuelve a seleccionar la categoría o el filtro.';
    return true;
  }

  async load(url, source) {
    if (!this.isConnected || this.cartBusy()) return;
    this.request?.abort();
    const request = this.request = new AbortController();
    this.lastAttempt = new URL(url);
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id', 'od-catalog-results');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) endpoint.searchParams.set('preview_theme_id', preview);
    const active = document.activeElement;
    const focus = this.results.contains(active) && active.name ? { name: active.name, value: active.value } : null;
    const openFacets = [...this.results.querySelectorAll('[data-od-facet][open]')].map(facet => facet.dataset.odFacet);
    this.results.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.odLoading;
    this.errorBox.hidden = true;
    try {
      const response = await fetch(endpoint, { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection response ${response.status}`);
      const html = await response.text();
      if (request.signal.aborted || !this.isConnected || this.request !== request) return;
      const result = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-od-catalog-result]');
      if (!result || this.resource(result.dataset.collectionUrl)?.pathname !== url.pathname) throw new Error('Missing collection result');
      // A cart request may have started while collection HTML was loading. Keep its DOM connected.
      if (this.cartBusy()) return;
      if (source === 'filter') {
        result.querySelectorAll('[data-od-facet]').forEach(facet => { facet.open = openFacets.includes(facet.dataset.odFacet); });
        const disclosure = result.querySelector('[data-od-filter-disclosure]');
        if (disclosure) disclosure.open = this.results.querySelector('[data-od-filter-disclosure]')?.open || false;
      }
      this.results.replaceChildren(document.importNode(result, true));
      this.completeSelection(url, result);
      if (source !== 'initial') {
        const restored = focus && [...this.results.querySelectorAll('[name]')].find(input => input.name === focus.name && input.value === focus.value);
        const target = restored?.closest('details:not([open])') ? this.results.querySelector('[data-od-filter-toggle]') : restored;
        (target || this.results.querySelector('[data-od-result-title]'))?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (request.signal.aborted || !this.isConnected || this.request !== request) return;
      this.errorBox.hidden = false;
      this.querySelector('[data-od-load-error-text]').textContent = this.dataset.loadError;
      this.querySelector('[data-od-fallback]').href = url;
      this.status.textContent = '';
    } finally {
      if (this.isConnected && this.request === request) this.results.removeAttribute('aria-busy');
    }
  }

  scheduleCategoryUpdate() {
    if (this.categoryFrame || !this.isConnected) return;
    this.categoryFrame = requestAnimationFrame(() => {
      this.categoryFrame = 0;
      if (this.isConnected) this.updateCategoryControls();
    });
  }

  updateCategoryControls() {
    if (!this.categories || !this.isConnected) return;
    const previous = this.categoryPrevious;
    const next = this.categoryNext;
    const controls = this.categoryControls;
    if (!controls || !previous || !next) return;
    const horizontal = this.categoryHorizontal();
    const position = horizontal ? this.categories.scrollLeft : this.categories.scrollTop;
    const extent = horizontal ? this.categories.scrollWidth : this.categories.scrollHeight;
    const visible = horizontal ? this.categories.clientWidth : this.categories.clientHeight;
    const hidden = extent <= visible + 2;
    const previousText = horizontal ? '←' : '↑';
    const nextText = horizontal ? '→' : '↓';
    const atStart = position <= 2;
    const atEnd = position >= extent - visible - 2;
    if (controls.hidden !== hidden) controls.hidden = hidden;
    if (previous.textContent !== previousText) previous.textContent = previousText;
    if (next.textContent !== nextText) next.textContent = nextText;
    if (previous.disabled !== atStart) previous.disabled = atStart;
    if (next.disabled !== atEnd) next.disabled = atEnd;
  }

  categoryHorizontal() { return window.innerWidth < 750; }

  scrollCategories(direction) {
    if (!this.categories) return;
    const motion = document.documentElement.dataset.odMotion !== 'false' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    const horizontal = this.categoryHorizontal();
    const size = horizontal ? this.categories.clientWidth : this.categories.clientHeight;
    this.categories.scrollBy({ [horizontal ? 'left' : 'top']: direction * size * .8, behavior: motion ? 'smooth' : 'auto' });
  }

  guardSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const wrapper = form?.closest('[data-od-native-form]');
    if (!wrapper || !this.contains(wrapper) || !customElements.get('product-form-component')) return;
    if (this.pending.has(form)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (event.defaultPrevented || !form.checkValidity()) return;
    const button = form.querySelector('[data-od-add]');
    if (!button || button.disabled) return;
    const record = { wrapper, button, label: button.getAttribute('aria-label'), serverError: '', timer: null, startedAt: Date.now() };
    this.pending.set(form, record);
    wrapper.setAttribute('data-od-pending', '');
    form.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-label', `Añadiendo… ${record.label}`);
    button.removeAttribute('data-od-added');
    wrapper.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
    record.timer = setTimeout(() => this.settle(form, false, this.dataset.odError), 30000);
  }

  observeCart(event) {
    const form = event.target.closest?.('[data-od-native-form]')?.querySelector('form');
    const record = form && this.pending.get(form);
    if (!record || !event.promise) return;
    // A timed-out request must not settle a newer submission on the same form.
    Promise.resolve(event.promise).then(
      result => { if (this.pending.get(form) === record) this.settle(form, !result?.detail?.didError, record.serverError || this.dataset.odError); },
      () => { if (this.pending.get(form) === record) this.settle(form, false, record.serverError || this.dataset.odError); }
    );
  }

  observeCartError(event) {
    const form = event.target.closest?.('[data-od-native-form]')?.querySelector('form');
    const record = form && this.pending.get(form);
    if (record) record.serverError = typeof event.detail?.description === 'string' ? event.detail.description : this.dataset.odError;
  }

  settle(form, successful, message) {
    const record = this.pending.get(form);
    if (!record) return;
    clearTimeout(record.timer);
    this.pending.delete(form);
    record.wrapper.removeAttribute('data-od-pending');
    form.removeAttribute('aria-busy');
    record.button.removeAttribute('aria-disabled');
    record.button.setAttribute('aria-label', record.label);
    const live = record.wrapper.querySelector('[ref="liveRegion"]');
    if (successful) {
      record.button.setAttribute('data-od-added', '');
      if (live) live.textContent = this.dataset.odSuccess;
      if (this.isConnected) this.status.textContent = this.dataset.odSuccess;
    } else {
      const notice = record.wrapper.querySelector('[ref="addToCartTextError"]');
      if (notice) { notice.textContent = message; notice.classList.remove('hidden'); }
      if (live) live.textContent = message;
      if (this.isConnected) this.status.textContent = message;
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.categoryFrame);
    this.categoryFrame = 0;
    for (const record of this.pending?.values() || []) { clearTimeout(record.timer); record.timer = null; }
  }
}

if (!customElements.get('od-catalog-browser')) customElements.define('od-catalog-browser', OdCatalogBrowser);
